import React, { useState, useEffect, useCallback, useRef } from 'react'
import styled from '@emotion/styled'
import { useNavigate, useLocation } from 'react-router-dom'
import { ConfirmationResult, isSignInWithEmailLink } from 'firebase/auth'
import { usePortalAuth, PORTAL_EMAIL_FOR_SIGN_IN_KEY } from '../hooks/usePortalAuth'
import { PhoneInputForm } from '../components/portal/PhoneInputForm'
import { EmailLoginForm } from '../components/portal/EmailLoginForm'
import { VerificationCodeForm } from '../components/portal/VerificationCodeForm'
import { PortalLoginLoadingContext, PortalLoginLoadingView } from '../components/portal/PortalLoginLoadingView'
import { requestPortalLoginSMS, requestPortalLoginEmailLink } from '../libs/CloudFunctionsApiHandler'
import { getGrooverText, formatGrooverText } from '../libs/GrooverTextRepository'
import useAppState from '../hooks/useAppState'
import { buildRoute } from '../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../pure-js/libs/RoutePath'
import { PortalLoginErrorStage, reportPortalLoginErrorToOps } from '../libs/ErrorHelper'
import {
  flushPortalLoginPerfError,
  flushPortalLoginPerfSendCodeMilestone,
  markPortalLoginPerf,
  startPortalLoginPerfFlow,
  withPortalLoginPerfSpan
} from '../libs/PortalLoginPerfHelper'
import {
  consumePortalAuthErrorCode,
  clearPendingPortalLoginContext,
  PortalAuthErrorCode,
  setPendingPortalLoginContext
} from '../libs/PortalLoginSessionHelper'

const PORTAL_EMAIL_LINK_FROM_KEY = 'portal_email_link_from'
import { auth } from '../libs/Firebase'
import GrooverText from '../components/GrooverText'
import { getPortalBrandingCssVars, usePortalBranding } from '../hooks/usePortalBranding'

type LoginStep = 'phone' | 'code' | 'loading' | 'error' | 'email' | 'emailSent' | 'emailLinkPrompt'

interface LoginState {
  step: LoginStep
  phoneNumber?: string
  errorMessage?: string
  confirmationResult?: ConfirmationResult
  loadingContext?: PortalLoginLoadingContext
  allowEmailAlternate?: boolean
}

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 24px;
  background: #ffffff;
`

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 400px;
  padding: 24px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid #ef4444;
  border-radius: 8px;
`

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 400px;
  padding: 24px;
  text-align: center;
`

const SuccessTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #000000;
  margin: 0;
`

const SuccessDescription = styled.p`
  font-size: 16px;
  color: #666666;
  margin: 0;
`

const ErrorMessage = styled.p`
  font-size: 16px;
  color: #ef4444;
  margin: 0;
  text-align: center;
`

const ActionButton = styled.button`
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  background: var(--portal-primary, #7c3aed);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: filter 0.2s;

  &:hover {
    filter: brightness(0.88);
  }
`

const SecondaryButton = styled(ActionButton)`
  color: var(--portal-primary, #7c3aed);
  background: transparent;
  border: 1px solid var(--portal-primary, #7c3aed);

  &:hover {
    background: rgba(var(--portal-primary-rgb, 124, 58, 237), 0.1);
    filter: none;
  }
`

const ButtonRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const EmailPromptForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 400px;
  width: 100%;
`

const EmailPromptInput = styled.input`
  padding: 12px 16px;
  font-size: 16px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  width: 100%;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: var(--portal-primary, #7c3aed);
  }
`

const BrandHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin-bottom: 32px;
`

const BrandName = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: var(--portal-primary, #7c3aed);
`

const BrandLogo = styled.img`
  display: block;
  max-height: 56px;
  max-width: 240px;
  object-fit: contain;
`

const SMS_RECAPTCHA_FAILURE_CODES = new Set([
  'auth/captcha-check-failed',
  'auth/invalid-app-credential',
  'auth/too-many-requests',
  'auth/quota-exceeded',
  'auth/operation-not-allowed'
])

const isRecaptchaClientError = (error: unknown): boolean => {
  const err = error as { code?: string; message?: string }
  if (err?.code === 'auth/captcha-check-failed' || err?.code === 'auth/invalid-app-credential') {
    return true
  }
  const message = err?.message ?? (error instanceof Error ? error.message : '')
  return message.includes('reCAPTCHA') || message.includes('already been rendered')
}

const isSmsRecaptchaFailure = (error: unknown): boolean => {
  if (isRecaptchaClientError(error)) {
    return true
  }
  const errorCode = (error as { code?: string })?.code
  if (!errorCode) {
    return true
  }
  if (errorCode === 'auth/invalid-phone-number') {
    return false
  }
  return SMS_RECAPTCHA_FAILURE_CODES.has(errorCode)
}

const cleanEmailLinkUrl = (): void => {
  const url = new URL(window.location.href)
  url.searchParams.delete('portalEmailLogin')
  url.searchParams.delete('apiKey')
  url.searchParams.delete('oobCode')
  url.searchParams.delete('mode')
  url.searchParams.delete('lang')
  window.history.replaceState({}, document.title, url.pathname + url.search)
}

export const PortalLoginPage: React.FC = () => {
  const [loginState, setLoginState] = useState<LoginState>({ step: 'phone' })
  const {
    login,
    sendVerificationCode,
    sendEmailSignInLink,
    completeEmailSignIn,
    portalUser,
    loading: portalAuthLoading,
    requestRecaptchaReinit
  } = usePortalAuth()
  const { state } = useAppState()
  const selectedLanguage = state.selectedLanguage
  const navigate = useNavigate()
  const location = useLocation()
  const [emailLinkPromptValue, setEmailLinkPromptValue] = useState('')
  const emailLinkHandledRef = useRef(false)
  const branding = usePortalBranding()

  useEffect(() => {
    requestRecaptchaReinit()
  }, [requestRecaptchaReinit])

  const handleCompleteEmailLink = useCallback(
    async (email: string) => {
      setLoginState({ step: 'loading', loadingContext: PortalLoginLoadingContext.CompleteEmailLink })

      try {
        await completeEmailSignIn(email)
      } catch (error: unknown) {
        console.error('Email link completion error:', error)
        const err = error as { code?: string }
        reportPortalLoginErrorToOps({
          stage: PortalLoginErrorStage.CompleteEmailLink,
          phoneNumber: '',
          error
        })
        setLoginState({
          step: 'error',
          errorMessage: getGrooverText('portalLoginEmailLinkError', selectedLanguage)
        })
        return
      } finally {
        cleanEmailLinkUrl()
      }
    },
    [completeEmailSignIn, selectedLanguage]
  )

  useEffect(() => {
    if (emailLinkHandledRef.current) {
      return
    }

    if (!isSignInWithEmailLink(auth, window.location.href)) {
      return
    }

    emailLinkHandledRef.current = true

    const storedEmail = (() => {
      try {
        return localStorage.getItem(PORTAL_EMAIL_FOR_SIGN_IN_KEY)
      } catch {
        return null
      }
    })()

    if (storedEmail) {
      void handleCompleteEmailLink(storedEmail)
    } else {
      setLoginState({ step: 'emailLinkPrompt' })
    }
  }, [handleCompleteEmailLink])

  useEffect(() => {
    if (portalAuthLoading) {
      return
    }
    const authErrorCode = consumePortalAuthErrorCode()
    if (authErrorCode === PortalAuthErrorCode.ClaimsSyncFailed) {
      setLoginState({
        step: 'error',
        errorMessage: getGrooverText('portalLoginClaimsSyncError', selectedLanguage)
      })
    }
  }, [portalAuthLoading, selectedLanguage])

  React.useEffect(() => {
    if (portalUser) {
      const fromState = (location.state as any)?.from?.pathname as string | undefined
      let fromStorage: string | null = null
      try {
        fromStorage = localStorage.getItem(PORTAL_EMAIL_LINK_FROM_KEY)
        if (fromStorage) localStorage.removeItem(PORTAL_EMAIL_LINK_FROM_KEY)
      } catch {
        // ignore private-mode / quota errors
      }
      const from = fromState || fromStorage || buildRoute(RoutePath.PORTAL_DASHBOARD)
      console.log('[PortalLoginPage] User authenticated, redirecting to:', from)
      navigate(from, { replace: true })
    }
  }, [portalUser, navigate, location])

  const handlePhoneSubmit = async (phoneNumber: string) => {
    const routeFrom = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname
    startPortalLoginPerfFlow(routeFrom)
    markPortalLoginPerf('sendCodeClick')
    setLoginState({ step: 'loading', loadingContext: PortalLoginLoadingContext.SendCode })

    let result: Awaited<ReturnType<typeof requestPortalLoginSMS>>
    try {
      result = await withPortalLoginPerfSpan('requestPortalLoginSMSMs', () => requestPortalLoginSMS({ phoneNumber }))
    } catch (error: unknown) {
      console.error('Phone submission error (requestPortalLoginSMS):', error)
      const err = error as { code?: string }
      void flushPortalLoginPerfError(PortalLoginErrorStage.RequestPortalLoginSMS, err?.code)
      reportPortalLoginErrorToOps({
        stage: PortalLoginErrorStage.RequestPortalLoginSMS,
        phoneNumber,
        error
      })
      setLoginState({
        step: 'error',
        errorMessage: getGrooverText('portalLoginSmsError', selectedLanguage)
      })
      return
    }

    if (!result.success) {
      let errorMessage: string
      if (result.messageId === 'portalLoginRateLimited' && result.minutesRemaining) {
        errorMessage = formatGrooverText(getGrooverText('portalLoginRateLimited', selectedLanguage), [
          result.minutesRemaining
        ])
      } else {
        errorMessage = result.messageId
          ? getGrooverText(result.messageId, selectedLanguage)
          : getGrooverText('portalLoginSmsError', selectedLanguage)
      }

      if (result.messageId !== 'portalLoginNoUser') {
        reportPortalLoginErrorToOps({
          stage: PortalLoginErrorStage.RequestPortalLoginSMS,
          phoneNumber,
          backendMessageId: result.messageId
        })
      }

      void flushPortalLoginPerfError(PortalLoginErrorStage.RequestPortalLoginSMS, result.messageId)

      setLoginState({
        step: 'error',
        errorMessage
      })
      return
    }

    if (result.enrollmentUserId) {
      setPendingPortalLoginContext(result.enrollmentUserId, phoneNumber)
    }

    try {
      const confirmationResult = await withPortalLoginPerfSpan('sendVerificationCodeMs', () =>
        sendVerificationCode(phoneNumber)
      )
      markPortalLoginPerf('codeScreenVisible')
      void flushPortalLoginPerfSendCodeMilestone()
      setLoginState({
        step: 'code',
        phoneNumber,
        confirmationResult
      })
    } catch (error: unknown) {
      console.error('Phone submission error (sendVerificationCode):', error)
      clearPendingPortalLoginContext()

      reportPortalLoginErrorToOps({
        stage: PortalLoginErrorStage.SendVerificationCode,
        phoneNumber,
        error
      })

      const err = error as { code?: string; message?: string }
      void flushPortalLoginPerfError(PortalLoginErrorStage.SendVerificationCode, err?.code)

      let errorMessage = getGrooverText('portalLoginSmsError', selectedLanguage)
      if (isRecaptchaClientError(error) && !err?.code) {
        errorMessage = getGrooverText('portalLoginRecaptchaError', selectedLanguage)
      } else if (err?.code === 'auth/invalid-app-credential') {
        console.error(
          'Firebase rejected the reCAPTCHA token. See docs/firebase-recaptcha-reset-guide.md if this persists.'
        )
        errorMessage = getGrooverText('portalLoginRecaptchaError', selectedLanguage)
      } else if (err?.code === 'auth/captcha-check-failed') {
        errorMessage = getGrooverText('portalLoginRecaptchaError', selectedLanguage)
      } else if (err?.code === 'auth/too-many-requests') {
        errorMessage = getGrooverText('portalLoginSmsTooManyRequests', selectedLanguage)
      } else if (err?.code === 'auth/quota-exceeded') {
        errorMessage = 'SMS quota exceeded. Please try again later.'
      } else if (err?.code === 'auth/invalid-phone-number') {
        errorMessage = getGrooverText('validationPhoneInvalid', selectedLanguage)
      }

      setLoginState({
        step: 'error',
        errorMessage,
        phoneNumber,
        allowEmailAlternate: isSmsRecaptchaFailure(error)
      })
    }
  }

  const handleCodeSubmit = async (code: string) => {
    if (!loginState.confirmationResult || !loginState.phoneNumber) {
      return
    }

    markPortalLoginPerf('enterCodeClick')
    setLoginState({ ...loginState, step: 'loading', loadingContext: PortalLoginLoadingContext.VerifyCode })

    try {
      await withPortalLoginPerfSpan('verifyCodeConfirmMs', () =>
        login(loginState.phoneNumber!, code, loginState.confirmationResult!)
      )
    } catch (error: unknown) {
      console.error('Code verification error:', error)
      const err = error as { code?: string }
      void flushPortalLoginPerfError(PortalLoginErrorStage.VerifyCode, err?.code)
      reportPortalLoginErrorToOps({
        stage: PortalLoginErrorStage.VerifyCode,
        phoneNumber: loginState.phoneNumber,
        error
      })
      setLoginState({
        ...loginState,
        step: 'error',
        errorMessage: getGrooverText('portalLoginCodeError', selectedLanguage)
      })
    }
  }

  const handleResend = async () => {
    if (loginState.phoneNumber) {
      await handlePhoneSubmit(loginState.phoneNumber)
    }
  }

  const handleRetry = () => {
    requestRecaptchaReinit()
    setLoginState({ step: 'phone' })
  }

  const handleUseEmailAlternate = () => {
    setLoginState({
      step: 'email',
      phoneNumber: loginState.phoneNumber
    })
  }

  const handleEmailSubmit = async (phoneNumber: string, email: string) => {
    setLoginState({
      step: 'loading',
      phoneNumber,
      loadingContext: PortalLoginLoadingContext.SendCode
    })

    let result: Awaited<ReturnType<typeof requestPortalLoginEmailLink>>
    try {
      result = await requestPortalLoginEmailLink({ phoneNumber, email })
    } catch (error: unknown) {
      console.error('Email link request error:', error)
      reportPortalLoginErrorToOps({
        stage: PortalLoginErrorStage.RequestPortalLoginEmailLink,
        phoneNumber,
        error
      })
      setLoginState({
        step: 'email',
        phoneNumber,
        errorMessage: getGrooverText('portalLoginEmailLinkError', selectedLanguage)
      })
      return
    }

    if (!result.success) {
      let errorMessage: string
      if (result.messageId === 'portalLoginRateLimited' && result.minutesRemaining) {
        errorMessage = formatGrooverText(getGrooverText('portalLoginRateLimited', selectedLanguage), [
          result.minutesRemaining
        ])
      } else if (result.messageId === 'portalLoginEmailPhoneMismatch') {
        errorMessage = getGrooverText('portalLoginEmailPhoneMismatch', selectedLanguage)
      } else if (result.messageId === 'portalLoginEmailLinkInvalidSource') {
        errorMessage = getGrooverText('portalLoginEmailLinkError', selectedLanguage)
      } else {
        errorMessage = result.messageId
          ? getGrooverText(result.messageId, selectedLanguage)
          : getGrooverText('portalLoginEmailLinkError', selectedLanguage)
      }

      if (result.messageId && result.messageId !== 'portalLoginNoUser') {
        reportPortalLoginErrorToOps({
          stage: PortalLoginErrorStage.RequestPortalLoginEmailLink,
          phoneNumber,
          backendMessageId: result.messageId
        })
      }

      setLoginState({
        step: 'email',
        phoneNumber,
        errorMessage
      })
      return
    }

    if (!result.enrollmentUserId) {
      setLoginState({
        step: 'email',
        phoneNumber,
        errorMessage: getGrooverText('portalLoginEmailLinkError', selectedLanguage)
      })
      return
    }

    try {
      const fromPath = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname
      try {
        if (fromPath) localStorage.setItem(PORTAL_EMAIL_LINK_FROM_KEY, fromPath)
      } catch {
        // ignore private-mode / quota errors
      }
      await sendEmailSignInLink(email, result.enrollmentUserId)
      setLoginState({ step: 'emailSent', phoneNumber })
    } catch (error: unknown) {
      console.error('sendEmailSignInLink error:', error)
      reportPortalLoginErrorToOps({
        stage: PortalLoginErrorStage.SendEmailLink,
        phoneNumber,
        error
      })
      setLoginState({
        step: 'email',
        phoneNumber,
        errorMessage: getGrooverText('portalLoginEmailLinkError', selectedLanguage)
      })
    }
  }

  const handleEmailLinkPromptSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const email = emailLinkPromptValue.trim()
    if (!email) {
      return
    }
    await handleCompleteEmailLink(email)
  }

  return (
    <LoginContainer style={getPortalBrandingCssVars(branding)}>
      <BrandHeader>
        {branding.logoUrl ? (
          <BrandLogo src={branding.logoUrl} alt={branding.name} />
        ) : (
          <BrandName>{branding.name}</BrandName>
        )}
      </BrandHeader>

      {loginState.step === 'phone' && <PhoneInputForm onSubmit={handlePhoneSubmit} />}

      {loginState.step === 'code' && loginState.phoneNumber && (
        <VerificationCodeForm
          phoneNumber={loginState.phoneNumber}
          onSubmit={handleCodeSubmit}
          onResend={handleResend}
        />
      )}

      {loginState.step === 'email' && (
        <EmailLoginForm
          initialPhoneNumber={loginState.phoneNumber}
          onSubmit={handleEmailSubmit}
          errorMessage={loginState.errorMessage}
        />
      )}

      {loginState.step === 'emailSent' && (
        <SuccessContainer>
          <SuccessTitle>
            <GrooverText textKey="portalLoginEmailSent" />
          </SuccessTitle>
          <SuccessDescription>
            <GrooverText textKey="portalLoginEmailSentDescription" />
          </SuccessDescription>
        </SuccessContainer>
      )}

      {loginState.step === 'emailLinkPrompt' && (
        <EmailPromptForm onSubmit={handleEmailLinkPromptSubmit}>
          <SuccessTitle>
            <GrooverText textKey="portalLoginEmailPrompt" />
          </SuccessTitle>
          <EmailPromptInput
            type="email"
            autoComplete="email"
            value={emailLinkPromptValue}
            onChange={(e) => setEmailLinkPromptValue(e.target.value)}
            required
          />
          <ActionButton type="submit">
            <GrooverText textKey="portalLoginEmailSendLink" />
          </ActionButton>
        </EmailPromptForm>
      )}

      {loginState.step === 'loading' && (
        <PortalLoginLoadingView context={loginState.loadingContext ?? PortalLoginLoadingContext.SendCode} />
      )}

      {loginState.step === 'error' && (
        <ErrorContainer>
          <ErrorMessage>{loginState.errorMessage}</ErrorMessage>
          <ButtonRow>
            <RetryButton onClick={handleRetry}>{getGrooverText('portalTryAgain', selectedLanguage)}</RetryButton>
            {loginState.allowEmailAlternate && (
              <SecondaryButton type="button" onClick={handleUseEmailAlternate}>
                {getGrooverText('portalLoginEmailAlternateCta', selectedLanguage)}
              </SecondaryButton>
            )}
          </ButtonRow>
        </ErrorContainer>
      )}

      <div id="recaptcha-mount" style={{ display: 'none' }} />
    </LoginContainer>
  )
}

const RetryButton = ActionButton
